import {action, autorun, makeObservable, observable, runInAction} from 'mobx';

import type {Destructible} from './types';

import {ReactionDisposeManager} from './ReactionDisposeManager';

export class ConditionalFlagManager implements Destructible {
  value: boolean;
  private readonly delay;
  private disposer = new ReactionDisposeManager();
  private inProgress = false;
  private time;
  private timer;

  constructor(initial = false, delay = 500) {
    this.value = initial;
    this.delay = delay;
    makeObservable<ConditionalFlagManager, 'start' | 'stop'>(this, {
      setValue: action.bound,
      start: action.bound,
      stop: action.bound,
      value: observable,
    });
  }

  destruct() {
    this.disposer.dispose();
    clearTimeout(this.timer);
  }

  setValue(value: boolean) {
    this.value = value;
  }

  trueWhen(predicate: () => boolean) {
    this.disposer.watch(
      '__autorun__',
      autorun(() => {
        const verdict = predicate();

        if (verdict) {
          if (this.inProgress) {
            clearTimeout(this.timer);
            this.start();
          } else {
            this.start();
          }
        } else {
          if (this.inProgress) {
            const diff = Date.now() - this.time;

            if (diff < this.delay) {
              runInAction(() => {
                this.timer = setTimeout(this.stop, this.delay - diff);
              });
            } else {
              this.stop();
            }
          }
        }
      }),
    );
  }

  private start() {
    this.time = Date.now();
    this.inProgress = true;
    this.setValue(true);
  }

  private stop() {
    this.setValue(false);
    this.inProgress = false;
    clearTimeout(this.timer);
    this.timer = null;
  }
}
